/*!
 * Parse the Javascript-like language used in data-bind and other HTML attributes (CSP-safe) 🥊  @tko/utils.parser@4.0.0-alpha8
 * (c) The Knockout.js Team - https://tko.io
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { unwrap, isWriteableObservable, isObservable } from '@tko/observable';
import { hasOwnProperty, options, objectForEach, clonePlainObjectDeep, extend, stringTrim } from '@tko/utils';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

function LAMBDA() { }
/**
 * @ operator - recursively call the identifier if it's a function
 * @param  {operand} a ignored
 * @param  {operand} b The variable to be called (if a function) and unwrapped
 * @return {value}   The result.
 */
function unwrapOrCall(a, b) {
    while (typeof b === 'function') {
        b = b();
    }
    return b;
}
var operators = {
    // unary
    '@': unwrapOrCall,
    '#': function (a, b) { return function () { return unwrap(b); }; },
    '=>': LAMBDA,
    '!': function not(a, b) { return !b; },
    '!!': function notnot(a, b) { return !!b; },
    '++': function preinc(a, b) { return ++b; },
    '--': function preinc(a, b) { return --b; },
    // mul/div
    '*': function mul(a, b) { return a * b; },
    '/': function div(a, b) { return a / b; },
    '%': function mod(a, b) { return a % b; },
    // sub/add
    '+': function add(a, b) { return a + b; },
    '-': function sub(a, b) { return (a || 0) - (b || 0); },
    '&-': function neg(a, b) { return -1 * b; },
    // relational
    '<': function lt(a, b) { return a < b; },
    '<=': function le(a, b) { return a <= b; },
    '>': function gt(a, b) { return a > b; },
    '>=': function ge(a, b) { return a >= b; },
    //    TODO: 'in': function (a, b) { return a in b; },
    //    TODO: 'instanceof': function (a, b) { return a instanceof b; },
    // equality
    '==': function equal(a, b) { return a === b; },
    '!=': function ne(a, b) { return a !== b; },
    '===': function sequal(a, b) { return a === b; },
    '!==': function sne(a, b) { return a !== b; },
    // bitwise
    '&': function bitAnd(a, b) { return a & b; },
    '^': function xor(a, b) { return a ^ b; },
    '|': function bitOr(a, b) { return a | b; },
    // logic
    '&&': function logicAnd(a, b) { return a && b; },
    '||': function logicOr(a, b) { return a || b; },
    // Access
    '.': function member(a, b) { return a[b]; },
    '[': function member(a, b) { return a[b]; },
    // conditional/ternary
    // '?': ternary See Node.js
    // Function-Call
    'call': function callOp(a, b) { return a.apply(null, b); }
};
/* Order of precedence from:
https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Operator_Precedence#Table
*/
// Our operator - unwrap/call
operators['@'].precedence = 21;
operators['#'].precedence = 21;
// lambda
operators['=>'].precedence = 20;
// Member
operators['.'].precedence = 19;
operators['['].precedence = 19;
// Logical not
operators['!'].precedence = 16;
operators['!!'].precedence = 16; // explicit double-negative
// Prefix inc/dec
operators['++'].precedence = 16;
operators['--'].precedence = 16;
operators['&-'].precedence = 16;
// mul/div/remainder
operators['%'].precedence = 14;
operators['*'].precedence = 14;
operators['/'].precedence = 14;
// add/sub
operators['+'].precedence = 13;
operators['-'].precedence = 13;
// bitwise
operators['|'].precedence = 12;
operators['^'].precedence = 11;
operators['&'].precedence = 10;
// comparison
operators['<'].precedence = 11;
operators['<='].precedence = 11;
operators['>'].precedence = 11;
operators['>='].precedence = 11;
// operators['in'].precedence = 8;
// operators['instanceof'].precedence = 8;
// equality
operators['=='].precedence = 10;
operators['!='].precedence = 10;
operators['==='].precedence = 10;
operators['!=='].precedence = 10;
// logic
operators['&&'].precedence = 6;
operators['||'].precedence = 5;
operators['&&'].earlyOut = function (a) { return !a; };
operators['||'].earlyOut = function (a) { return a; };
// Call a function
operators['call'].precedence = 1;

var IS_EXPR_OR_IDENT = Symbol('Node - Is Expression Or Identifier');
var Node = /** @class */ (function () {
    function Node(lhs, op, rhs) {
        this.lhs = lhs;
        this.op = op;
        this.rhs = rhs;
    }
    Object.defineProperty(Node, "operators", {
        get: function () { return operators; },
        enumerable: true,
        configurable: true
    });
    Node.prototype.get_leaf_value = function (leaf, context, globals, node) {
        if (typeof leaf === 'function') {
            // Expressions on observables are nonsensical, so we unwrap any
            // function values (e.g. identifiers).
            return unwrap(leaf());
        }
        // primitives
        if (typeof leaf !== 'object' || leaf === null) {
            return leaf;
        }
        // Identifiers and Expressions
        if (leaf[Node.isExpressionOrIdentifierSymbol]) {
            // lhs is passed in as the parent of the leaf. It will be defined in
            // cases like a.b.c as 'a' for 'b' then as 'b' for 'c'.
            return unwrap(leaf.get_value(undefined, context, globals, node));
        }
        // Plain object/class.
        return leaf;
    };
    /**
     * Return a function that calculates and returns an expression's value
     * when called.
     * @param  {array} ops  The operations to perform
     * @return {function}   The function that calculates the expression.
     *
     * Note that for a lambda, we do not evaluate the RHS expression until
     * the lambda is called.
     */
    Node.prototype.get_value = function (notused, context, globals, node) {
        var node = this;
        if (node.op === LAMBDA) {
            return function () { return node.get_leaf_value(node.rhs, context, globals, node); };
        }
        var lhv = node.get_leaf_value(node.lhs, context, globals, node);
        var earlyOut = node.op.earlyOut;
        if (earlyOut && earlyOut(lhv)) {
            return lhv;
        }
        var rhv = node.get_leaf_value(node.rhs, context, globals, node);
        return node.op(lhv, rhv, context, globals);
    };
    Object.defineProperty(Node, "isExpressionOrIdentifierSymbol", {
        //
        // Class variables.
        //
        get: function () { return IS_EXPR_OR_IDENT; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Node.prototype, IS_EXPR_OR_IDENT, {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    Node.value_of = function (item, context, globals, node) {
        if (item && item[Node.isExpressionOrIdentifierSymbol]) {
            return item.get_value(item, context, globals, node);
        }
        return item;
    };
    /**
    *  Convert an array of nodes to an executable tree.
    *  @return {object} An object with a `lhs`, `rhs` and `op` key, corresponding
    *                      to the left hand side, right hand side, and
    *                      operation function.
    */
    Node.create_root = function (nodes) {
        var root, leaf, op, value;
        // Prime the leaf = root node.
        leaf = root = new Node(nodes.shift(), nodes.shift(), nodes.shift());
        while (true) {
            op = nodes.shift();
            value = nodes.shift();
            if (!op) {
                break;
            }
            if (op.precedence < root.op.precedence) {
                // rebase
                root = new Node(root, op, value);
                leaf = root;
            }
            else {
                leaf.rhs = new Node(leaf.rhs, op, value);
                leaf = leaf.rhs;
            }
        }
        // console.log('tree', root)
        return root;
    };
    return Node;
}());
/**
 * Because of cyclical dependencies on operators <-> Node <-> value_of,
 * we need to patch this in here.
 */
operators['?'] = function ternary(a, b, context, globals, node) {
    return Node.value_of(a ? b.yes : b.no, context, globals, node);
};
operators['?'].precedence = 4;

var Expression = /** @class */ (function () {
    function Expression(nodes) {
        this.nodes = nodes;
        this.root = Node.create_root(nodes);
    }
    /**
     * Return the value of `this` Expression instance.
     */
    Expression.prototype.get_value = function (parent, context, globals, node) {
        if (!this.root) {
            this.root = Node.create_root(this.nodes);
        }
        return this.root.get_value(parent, context, globals, node);
    };
    return Expression;
}());
Expression.prototype[Node.isExpressionOrIdentifierSymbol] = true;

var Arguments = /** @class */ (function () {
    function Arguments(parser, args) {
        this.parser = parser;
        this.args = args;
    }
    Arguments.prototype.get_value = function (parent, context, globals, node) {
        var deReffedArgs = [];
        for (var i = 0, j = this.args.length; i < j; ++i) {
            deReffedArgs.push(Node.value_of(this.args[i], context, globals, node));
        }
        return deReffedArgs;
    };
    Object.defineProperty(Arguments.prototype, Node.isExpressionOrIdentifierSymbol, {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    return Arguments;
}());

/**
 * The following regular expressions were generated by
 *  https://mathiasbynens.be/demo/javascript-identifier-regex
 */
var IDStart = /[\$A-Z_a-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0370-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u048A-\u052F\u0531-\u0556\u0559\u0561-\u0587\u05D0-\u05EA\u05F0-\u05F2\u0620-\u064A\u066E\u066F\u0671-\u06D3\u06D5\u06E5\u06E6\u06EE\u06EF\u06FA-\u06FC\u06FF\u0710\u0712-\u072F\u074D-\u07A5\u07B1\u07CA-\u07EA\u07F4\u07F5\u07FA\u0800-\u0815\u081A\u0824\u0828\u0840-\u0858\u08A0-\u08B4\u0904-\u0939\u093D\u0950\u0958-\u0961\u0971-\u0980\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BD\u09CE\u09DC\u09DD\u09DF-\u09E1\u09F0\u09F1\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A59-\u0A5C\u0A5E\u0A72-\u0A74\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABD\u0AD0\u0AE0\u0AE1\u0AF9\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3D\u0B5C\u0B5D\u0B5F-\u0B61\u0B71\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BD0\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D\u0C58-\u0C5A\u0C60\u0C61\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBD\u0CDE\u0CE0\u0CE1\u0CF1\u0CF2\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D\u0D4E\u0D5F-\u0D61\u0D7A-\u0D7F\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0E01-\u0E30\u0E32\u0E33\u0E40-\u0E46\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB0\u0EB2\u0EB3\u0EBD\u0EC0-\u0EC4\u0EC6\u0EDC-\u0EDF\u0F00\u0F40-\u0F47\u0F49-\u0F6C\u0F88-\u0F8C\u1000-\u102A\u103F\u1050-\u1055\u105A-\u105D\u1061\u1065\u1066\u106E-\u1070\u1075-\u1081\u108E\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16EE-\u16F8\u1700-\u170C\u170E-\u1711\u1720-\u1731\u1740-\u1751\u1760-\u176C\u176E-\u1770\u1780-\u17B3\u17D7\u17DC\u1820-\u1877\u1880-\u18A8\u18AA\u18B0-\u18F5\u1900-\u191E\u1950-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u1A00-\u1A16\u1A20-\u1A54\u1AA7\u1B05-\u1B33\u1B45-\u1B4B\u1B83-\u1BA0\u1BAE\u1BAF\u1BBA-\u1BE5\u1C00-\u1C23\u1C4D-\u1C4F\u1C5A-\u1C7D\u1CE9-\u1CEC\u1CEE-\u1CF1\u1CF5\u1CF6\u1D00-\u1DBF\u1E00-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u2071\u207F\u2090-\u209C\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2160-\u2188\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CEE\u2CF2\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D80-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2E2F\u3005-\u3007\u3021-\u3029\u3031-\u3035\u3038-\u303C\u3041-\u3096\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FD5\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA61F\uA62A\uA62B\uA640-\uA66E\uA67F-\uA69D\uA6A0-\uA6EF\uA717-\uA71F\uA722-\uA788\uA78B-\uA7AD\uA7B0-\uA7B7\uA7F7-\uA801\uA803-\uA805\uA807-\uA80A\uA80C-\uA822\uA840-\uA873\uA882-\uA8B3\uA8F2-\uA8F7\uA8FB\uA8FD\uA90A-\uA925\uA930-\uA946\uA960-\uA97C\uA984-\uA9B2\uA9CF\uA9E0-\uA9E4\uA9E6-\uA9EF\uA9FA-\uA9FE\uAA00-\uAA28\uAA40-\uAA42\uAA44-\uAA4B\uAA60-\uAA76\uAA7A\uAA7E-\uAAAF\uAAB1\uAAB5\uAAB6\uAAB9-\uAABD\uAAC0\uAAC2\uAADB-\uAADD\uAAE0-\uAAEA\uAAF2-\uAAF4\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB65\uAB70-\uABE2\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D\uFB1F-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE70-\uFE74\uFE76-\uFEFC\uFF21-\uFF3A\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]/;
var IDContinue = /[\$0-9A-Z_a-z\xAA\xB5\xBA\xC0-\xD6\xD8-\xF6\xF8-\u02C1\u02C6-\u02D1\u02E0-\u02E4\u02EC\u02EE\u0300-\u0374\u0376\u0377\u037A-\u037D\u037F\u0386\u0388-\u038A\u038C\u038E-\u03A1\u03A3-\u03F5\u03F7-\u0481\u0483-\u0487\u048A-\u052F\u0531-\u0556\u0559\u0561-\u0587\u0591-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7\u05D0-\u05EA\u05F0-\u05F2\u0610-\u061A\u0620-\u0669\u066E-\u06D3\u06D5-\u06DC\u06DF-\u06E8\u06EA-\u06FC\u06FF\u0710-\u074A\u074D-\u07B1\u07C0-\u07F5\u07FA\u0800-\u082D\u0840-\u085B\u08A0-\u08B4\u08E3-\u0963\u0966-\u096F\u0971-\u0983\u0985-\u098C\u098F\u0990\u0993-\u09A8\u09AA-\u09B0\u09B2\u09B6-\u09B9\u09BC-\u09C4\u09C7\u09C8\u09CB-\u09CE\u09D7\u09DC\u09DD\u09DF-\u09E3\u09E6-\u09F1\u0A01-\u0A03\u0A05-\u0A0A\u0A0F\u0A10\u0A13-\u0A28\u0A2A-\u0A30\u0A32\u0A33\u0A35\u0A36\u0A38\u0A39\u0A3C\u0A3E-\u0A42\u0A47\u0A48\u0A4B-\u0A4D\u0A51\u0A59-\u0A5C\u0A5E\u0A66-\u0A75\u0A81-\u0A83\u0A85-\u0A8D\u0A8F-\u0A91\u0A93-\u0AA8\u0AAA-\u0AB0\u0AB2\u0AB3\u0AB5-\u0AB9\u0ABC-\u0AC5\u0AC7-\u0AC9\u0ACB-\u0ACD\u0AD0\u0AE0-\u0AE3\u0AE6-\u0AEF\u0AF9\u0B01-\u0B03\u0B05-\u0B0C\u0B0F\u0B10\u0B13-\u0B28\u0B2A-\u0B30\u0B32\u0B33\u0B35-\u0B39\u0B3C-\u0B44\u0B47\u0B48\u0B4B-\u0B4D\u0B56\u0B57\u0B5C\u0B5D\u0B5F-\u0B63\u0B66-\u0B6F\u0B71\u0B82\u0B83\u0B85-\u0B8A\u0B8E-\u0B90\u0B92-\u0B95\u0B99\u0B9A\u0B9C\u0B9E\u0B9F\u0BA3\u0BA4\u0BA8-\u0BAA\u0BAE-\u0BB9\u0BBE-\u0BC2\u0BC6-\u0BC8\u0BCA-\u0BCD\u0BD0\u0BD7\u0BE6-\u0BEF\u0C00-\u0C03\u0C05-\u0C0C\u0C0E-\u0C10\u0C12-\u0C28\u0C2A-\u0C39\u0C3D-\u0C44\u0C46-\u0C48\u0C4A-\u0C4D\u0C55\u0C56\u0C58-\u0C5A\u0C60-\u0C63\u0C66-\u0C6F\u0C81-\u0C83\u0C85-\u0C8C\u0C8E-\u0C90\u0C92-\u0CA8\u0CAA-\u0CB3\u0CB5-\u0CB9\u0CBC-\u0CC4\u0CC6-\u0CC8\u0CCA-\u0CCD\u0CD5\u0CD6\u0CDE\u0CE0-\u0CE3\u0CE6-\u0CEF\u0CF1\u0CF2\u0D01-\u0D03\u0D05-\u0D0C\u0D0E-\u0D10\u0D12-\u0D3A\u0D3D-\u0D44\u0D46-\u0D48\u0D4A-\u0D4E\u0D57\u0D5F-\u0D63\u0D66-\u0D6F\u0D7A-\u0D7F\u0D82\u0D83\u0D85-\u0D96\u0D9A-\u0DB1\u0DB3-\u0DBB\u0DBD\u0DC0-\u0DC6\u0DCA\u0DCF-\u0DD4\u0DD6\u0DD8-\u0DDF\u0DE6-\u0DEF\u0DF2\u0DF3\u0E01-\u0E3A\u0E40-\u0E4E\u0E50-\u0E59\u0E81\u0E82\u0E84\u0E87\u0E88\u0E8A\u0E8D\u0E94-\u0E97\u0E99-\u0E9F\u0EA1-\u0EA3\u0EA5\u0EA7\u0EAA\u0EAB\u0EAD-\u0EB9\u0EBB-\u0EBD\u0EC0-\u0EC4\u0EC6\u0EC8-\u0ECD\u0ED0-\u0ED9\u0EDC-\u0EDF\u0F00\u0F18\u0F19\u0F20-\u0F29\u0F35\u0F37\u0F39\u0F3E-\u0F47\u0F49-\u0F6C\u0F71-\u0F84\u0F86-\u0F97\u0F99-\u0FBC\u0FC6\u1000-\u1049\u1050-\u109D\u10A0-\u10C5\u10C7\u10CD\u10D0-\u10FA\u10FC-\u1248\u124A-\u124D\u1250-\u1256\u1258\u125A-\u125D\u1260-\u1288\u128A-\u128D\u1290-\u12B0\u12B2-\u12B5\u12B8-\u12BE\u12C0\u12C2-\u12C5\u12C8-\u12D6\u12D8-\u1310\u1312-\u1315\u1318-\u135A\u135D-\u135F\u1380-\u138F\u13A0-\u13F5\u13F8-\u13FD\u1401-\u166C\u166F-\u167F\u1681-\u169A\u16A0-\u16EA\u16EE-\u16F8\u1700-\u170C\u170E-\u1714\u1720-\u1734\u1740-\u1753\u1760-\u176C\u176E-\u1770\u1772\u1773\u1780-\u17D3\u17D7\u17DC\u17DD\u17E0-\u17E9\u180B-\u180D\u1810-\u1819\u1820-\u1877\u1880-\u18AA\u18B0-\u18F5\u1900-\u191E\u1920-\u192B\u1930-\u193B\u1946-\u196D\u1970-\u1974\u1980-\u19AB\u19B0-\u19C9\u19D0-\u19D9\u1A00-\u1A1B\u1A20-\u1A5E\u1A60-\u1A7C\u1A7F-\u1A89\u1A90-\u1A99\u1AA7\u1AB0-\u1ABD\u1B00-\u1B4B\u1B50-\u1B59\u1B6B-\u1B73\u1B80-\u1BF3\u1C00-\u1C37\u1C40-\u1C49\u1C4D-\u1C7D\u1CD0-\u1CD2\u1CD4-\u1CF6\u1CF8\u1CF9\u1D00-\u1DF5\u1DFC-\u1F15\u1F18-\u1F1D\u1F20-\u1F45\u1F48-\u1F4D\u1F50-\u1F57\u1F59\u1F5B\u1F5D\u1F5F-\u1F7D\u1F80-\u1FB4\u1FB6-\u1FBC\u1FBE\u1FC2-\u1FC4\u1FC6-\u1FCC\u1FD0-\u1FD3\u1FD6-\u1FDB\u1FE0-\u1FEC\u1FF2-\u1FF4\u1FF6-\u1FFC\u200C\u200D\u203F\u2040\u2054\u2071\u207F\u2090-\u209C\u20D0-\u20DC\u20E1\u20E5-\u20F0\u2102\u2107\u210A-\u2113\u2115\u2119-\u211D\u2124\u2126\u2128\u212A-\u212D\u212F-\u2139\u213C-\u213F\u2145-\u2149\u214E\u2160-\u2188\u2C00-\u2C2E\u2C30-\u2C5E\u2C60-\u2CE4\u2CEB-\u2CF3\u2D00-\u2D25\u2D27\u2D2D\u2D30-\u2D67\u2D6F\u2D7F-\u2D96\u2DA0-\u2DA6\u2DA8-\u2DAE\u2DB0-\u2DB6\u2DB8-\u2DBE\u2DC0-\u2DC6\u2DC8-\u2DCE\u2DD0-\u2DD6\u2DD8-\u2DDE\u2DE0-\u2DFF\u2E2F\u3005-\u3007\u3021-\u302F\u3031-\u3035\u3038-\u303C\u3041-\u3096\u3099\u309A\u309D-\u309F\u30A1-\u30FA\u30FC-\u30FF\u3105-\u312D\u3131-\u318E\u31A0-\u31BA\u31F0-\u31FF\u3400-\u4DB5\u4E00-\u9FD5\uA000-\uA48C\uA4D0-\uA4FD\uA500-\uA60C\uA610-\uA62B\uA640-\uA66F\uA674-\uA67D\uA67F-\uA6F1\uA717-\uA71F\uA722-\uA788\uA78B-\uA7AD\uA7B0-\uA7B7\uA7F7-\uA827\uA840-\uA873\uA880-\uA8C4\uA8D0-\uA8D9\uA8E0-\uA8F7\uA8FB\uA8FD\uA900-\uA92D\uA930-\uA953\uA960-\uA97C\uA980-\uA9C0\uA9CF-\uA9D9\uA9E0-\uA9FE\uAA00-\uAA36\uAA40-\uAA4D\uAA50-\uAA59\uAA60-\uAA76\uAA7A-\uAAC2\uAADB-\uAADD\uAAE0-\uAAEF\uAAF2-\uAAF6\uAB01-\uAB06\uAB09-\uAB0E\uAB11-\uAB16\uAB20-\uAB26\uAB28-\uAB2E\uAB30-\uAB5A\uAB5C-\uAB65\uAB70-\uABEA\uABEC\uABED\uABF0-\uABF9\uAC00-\uD7A3\uD7B0-\uD7C6\uD7CB-\uD7FB\uF900-\uFA6D\uFA70-\uFAD9\uFB00-\uFB06\uFB13-\uFB17\uFB1D-\uFB28\uFB2A-\uFB36\uFB38-\uFB3C\uFB3E\uFB40\uFB41\uFB43\uFB44\uFB46-\uFBB1\uFBD3-\uFD3D\uFD50-\uFD8F\uFD92-\uFDC7\uFDF0-\uFDFB\uFE00-\uFE0F\uFE20-\uFE2F\uFE33\uFE34\uFE4D-\uFE4F\uFE70-\uFE74\uFE76-\uFEFC\uFF10-\uFF19\uFF21-\uFF3A\uFF3F\uFF41-\uFF5A\uFF66-\uFFBE\uFFC2-\uFFC7\uFFCA-\uFFCF\uFFD2-\uFFD7\uFFDA-\uFFDC]/;

var Identifier = /** @class */ (function () {
    function Identifier(parser, token, dereferences) {
        this.token = token;
        this.dereferences = dereferences;
        this.parser = parser;
    }
    /**
     * Apply all () and [] functions on the identifier to the lhs value e.g.
     * a()[3] has deref functions that are essentially this:
     *     [_deref_call, _deref_this where this=3]
     *
     * @param  {mixed} value  Should be an object.
     * @return {mixed}        The dereferenced value.
     *
     * [1] We want to bind any function that is a method of an object, but not
     *     corrupt any values (e.g. computed()s).   e.g. Running x.bind(obj) where
     *     we're given `data-bind='binding: obj.x'` and x is a computed will
     *     break the computed's `this` and it will stop working as expected.
     *
     *     The test `!last_value.hasOwnProperty(member)`
     *     distinguishes between functions on the prototype chain (prototypal
     *     members) and value-members added directly to the object.  This may
     *     not be the canonical test for this relationship, but it succeeds
     *     in the known test cases.
     *
     *     See: `this` tests of our dereference function.
     */
    Identifier.prototype.dereference = function (value, $context, globals, node) {
        var member;
        var refs = this.dereferences || [];
        var $data = $context.$data || {};
        var lastValue; // becomes `this` in function calls to object properties.
        var i, n;
        for (i = 0, n = refs.length; i < n; ++i) {
            member = Node.value_of(refs[i], $context, globals, node);
            if (typeof value === 'function' && refs[i] instanceof Arguments) {
                // fn(args)
                value = value.apply(lastValue || $data, member);
                lastValue = value;
            }
            else {
                // obj[x] or obj.x dereference.  Note that obj may be a function.
                lastValue = value;
                value = Node.value_of(value[member], $context, globals, node);
            }
        }
        // [1] See note above.
        if (typeof value === 'function' && n > 0 && lastValue !== value &&
            !hasOwnProperty(lastValue, member)) {
            return value.bind(lastValue);
        }
        return value;
    };
    /**
     * Return the value as one would get it from the top-level i.e.
     * $data.token/$context.token/globals.token; this does not return intermediate
     * values on a chain of members i.e. $data.hello.there -- requesting the
     * Identifier('there').value will return $data/$context/globals.there.
     *
     * This will dereference using () or [arg] member.
     * @param  {object | Identifier | Expression} parent
     * @return {mixed}  Return the primitive or an accessor.
     */
    Identifier.prototype.get_value = function (parent, context, globals, node) {
        var intermediate = parent && !(parent instanceof Identifier)
            ? Node.value_of(parent, context, globals, node)[this.token]
            : context.lookup(this.token, globals, node);
        return this.dereference(intermediate, context, globals, node);
    };
    Identifier.prototype.assign = function (object, property, value) {
        if (isWriteableObservable(object[property])) {
            object[property](value);
        }
        else if (!isObservable(object[property])) {
            object[property] = value;
        }
    };
    /**
     * Set the value of the Identifier.
     *
     * @param {Mixed} new_value The value that Identifier is to be set to.
     */
    Identifier.prototype.set_value = function (new_value, $context, globals) {
        var $data = $context.$data || {};
        var refs = this.dereferences || [];
        var leaf = this.token;
        var i, n, root;
        if (hasOwnProperty($data, leaf)) {
            root = $data;
        }
        else if (hasOwnProperty($context, leaf)) {
            root = $context;
        }
        else if (hasOwnProperty(globals, leaf)) {
            root = globals;
        }
        else {
            throw new Error('Identifier::set_value -- ' +
                "The property '" + leaf + "' does not exist " +
                'on the $data, $context, or globals.');
        }
        // Degenerate case. {$data|$context|global}[leaf] = something;
        n = refs.length;
        if (n === 0) {
            this.assign(root, leaf, new_value);
            return;
        }
        // First dereference is {$data|$context|global}[token].
        root = root[leaf];
        // We cannot use this.dereference because that gives the leaf; to evoke
        // the ES5 setter we have to call `obj[leaf] = new_value`
        for (i = 0; i < n - 1; ++i) {
            leaf = refs[i];
            if (leaf instanceof Arguments) {
                root = root();
            }
            else {
                root = root[Node.value_of(leaf)];
            }
        }
        // We indicate that a dereference is a function when it is `true`.
        if (refs[i] === true) {
            throw new Error('Cannot assign a value to a function.');
        }
        // Call the setter for the leaf.
        if (refs[i]) {
            this.assign(root, Node.value_of(refs[i]), new_value);
        }
    };
    /**
     * Determine if a character is a valid item in an identifier.
     * Note that we do not check whether the first item is a number, nor do we
     * support unicode identifiers here.
     *
     * From:  http://stackoverflow.com/a/9337047
     * @param  {String}  ch  The character
     * @return {Boolean}     True if this is a valid identifier
     */
    // function is_identifier_char(ch) {
    //   return (ch >= 'A' && ch <= 'Z') ||
    //          (ch >= 'a' && ch <= 'z') ||
    //          (ch >= '0' && ch <= 9) ||
    //           ch === '_' || ch === '$';
    // }
    Identifier.is_valid_start_char = function (ch) {
        return IDStart.test(ch);
    };
    Identifier.is_valid_continue_char = function (ch) {
        return IDContinue.test(ch);
    };
    Object.defineProperty(Identifier.prototype, Node.isExpressionOrIdentifierSymbol, {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    return Identifier;
}());

var Ternary = /** @class */ (function () {
    function Ternary(yes, no) {
        Object.assign(this, { yes: yes, no: no });
    }
    Ternary.prototype.get_value = function () { return this; };
    Object.defineProperty(Ternary.prototype, Node.isExpressionOrIdentifierSymbol, {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    return Ternary;
}());

/**
 * Originally based on (public domain):
 * https://github.com/douglascrockford/JSON-js/blob/master/json_parse.js
 */
var escapee = {
    "'": "'",
    '"': '"',
    '`': '`',
    '\\': '\\',
    '/': '/',
    '$': '$',
    b: '\b',
    f: '\f',
    n: '\n',
    r: '\r',
    t: '\t'
};
/**
 * Construct a new Parser instance with new Parser(node, context)
 * @param {Node} node    The DOM element from which we parsed the
 *                         content.
 * @param {object} context The Knockout context.
 * @param {object} globals An object containing any desired globals.
 */
var Parser = /** @class */ (function () {
    function Parser() {
    }
    Parser.prototype.white = function () {
        var ch = this.ch;
        while (ch && ch <= ' ') {
            ch = this.next();
        }
        return this.comment(ch);
    };
    /**
     * Slurp any C or C++ style comments
     */
    Parser.prototype.comment = function (ch) {
        if (ch !== '/') {
            return ch;
        }
        var p = this.at;
        var second = this.lookahead();
        if (second === '/') {
            while (ch) {
                ch = this.next();
                if (ch === '\n' || ch === '\r') {
                    break;
                }
            }
            ch = this.next();
        }
        else if (second === '*') {
            while (ch) {
                ch = this.next();
                if (ch === '*' && this.lookahead() === '/') {
                    this.next();
                    break;
                }
            }
            if (!ch) {
                this.error('Unclosed comment, starting at character ' + p);
            }
            this.next();
            return this.white();
        }
        return ch;
    };
    Parser.prototype.next = function (c) {
        if (c && c !== this.ch) {
            this.error("Expected '" + c + "' but got '" + this.ch + "'");
        }
        this.ch = this.text.charAt(this.at);
        this.at += 1;
        return this.ch;
    };
    Parser.prototype.lookahead = function () {
        return this.text[this.at];
    };
    Parser.prototype.error = function (m) {
        if (m instanceof Error) {
            throw m;
        }
        var _a = __read(m.name ? [m.name, m.message] : [m, ''], 2), name = _a[0], msg = _a[1];
        var message = "\n" + name + " " + msg + " of\n    " + this.text + "\n" + Array(this.at).join(' ') + '_/ 🔥 \\_\n';
        throw new Error(message);
    };
    Parser.prototype.name = function () {
        // A name of a binding
        var name = '';
        var enclosedBy;
        this.white();
        var ch = this.ch;
        if (ch === "'" || ch === '"') {
            enclosedBy = ch;
            ch = this.next();
        }
        while (ch) {
            if (enclosedBy && ch === enclosedBy) {
                this.white();
                ch = this.next();
                if (ch !== ':' && ch !== ',') {
                    this.error('Object name: ' + name + ' missing closing ' + enclosedBy);
                }
                return name;
            }
            else if (ch === ':' || ch <= ' ' || ch === ',' || ch === '|') {
                return name;
            }
            name += ch;
            ch = this.next();
        }
        return name;
    };
    Parser.prototype.number = function () {
        var number;
        var string = '';
        var ch = this.ch;
        if (ch === '-') {
            string = '-';
            ch = this.next('-');
        }
        while (ch >= '0' && ch <= '9') {
            string += ch;
            ch = this.next();
        }
        if (ch === '.') {
            string += '.';
            ch = this.next();
            while (ch && ch >= '0' && ch <= '9') {
                string += ch;
                ch = this.next();
            }
        }
        if (ch === 'e' || ch === 'E') {
            string += ch;
            ch = this.next();
            if (ch === '-' || ch === '+') {
                string += ch;
                ch = this.next();
            }
            while (ch >= '0' && ch <= '9') {
                string += ch;
                ch = this.next();
            }
        }
        number = +string;
        if (!isFinite(number)) {
            options.onError(new Error('Bad number: ' + number + ' in ' + string));
        }
        else {
            return number;
        }
    };
    /**
     * Add a property to 'object' that equals the given value.
     * @param  {Object} object The object to add the value to.
     * @param  {String} key    object[key] is set to the given value.
     * @param  {mixed}  value  The value, may be a primitive or a function. If a
     *                         function it is unwrapped as a property.
     */
    Parser.prototype.objectAddValue = function (object, key, value) {
        var _this = this;
        if (value && value[Node.isExpressionOrIdentifierSymbol]) {
            Object.defineProperty(object, key, {
                get: function () { return Node.value_of.apply(Node, __spread([value], _this.currentContextGlobals)); },
                enumerable: true
            });
        }
        else if (Array.isArray(value)) {
            Object.defineProperty(object, key, {
                get: function () { return value.map(function (v) { return Node.value_of.apply(Node, __spread([v], _this.currentContextGlobals)); }); },
                enumerable: true
            });
        }
        else {
            // primitives
            object[key] = value;
        }
    };
    Parser.prototype.object = function () {
        var key;
        var object = {};
        var ch = this.ch;
        if (ch === '{') {
            this.next('{');
            ch = this.white();
            if (ch === '}') {
                ch = this.next('}');
                return object;
            }
            while (ch) {
                if (ch === '"' || ch === "'" || ch === '`') {
                    key = this.string();
                }
                else {
                    key = this.name();
                }
                if (hasOwnProperty(object, key)) {
                    this.error('Duplicate key "' + key + '"');
                }
                if (this.white() === ':') {
                    ch = this.next(':');
                    this.objectAddValue(object, key, this.expression());
                }
                else {
                    var objectKeyIsValue = new Identifier(this, key, []);
                    this.objectAddValue(object, key, objectKeyIsValue);
                }
                ch = this.white();
                if (ch === '}') {
                    ch = this.next('}');
                    return object;
                }
                this.next(',');
                ch = this.white();
            }
        }
        this.error('Bad object');
    };
    /**
     * Read up to delim and return the string
     * @param  {string} delim The delimiter, either ' or "
     * @return {string}       The string read.
     */
    Parser.prototype.readString = function (delim) {
        var string = '';
        var nodes = [''];
        var plusOp = operators['+'];
        var hex;
        var i;
        var uffff;
        var interpolate = delim === '`';
        var ch = this.next();
        while (ch) {
            if (ch === delim) {
                ch = this.next();
                if (interpolate) {
                    nodes.push(plusOp);
                }
                nodes.push(string);
                return nodes;
            }
            if (ch === '\\') {
                ch = this.next();
                if (ch === 'u') {
                    uffff = 0;
                    for (i = 0; i < 4; i += 1) {
                        hex = parseInt(ch = this.next(), 16);
                        if (!isFinite(hex)) {
                            break;
                        }
                        uffff = uffff * 16 + hex;
                    }
                    string += String.fromCharCode(uffff);
                }
                else if (typeof escapee[ch] === 'string') {
                    string += escapee[ch];
                }
                else {
                    break;
                }
            }
            else if (interpolate && ch === '$') {
                ch = this.next();
                if (ch === '{') {
                    this.next('{');
                    nodes.push(plusOp);
                    nodes.push(string);
                    nodes.push(plusOp);
                    nodes.push(this.expression());
                    string = '';
                    // this.next('}');
                }
                else {
                    string += '$' + ch;
                }
            }
            else {
                string += ch;
            }
            ch = this.next();
        }
        this.error('Bad string');
    };
    Parser.prototype.string = function () {
        var ch = this.ch;
        if (ch === '"') {
            return this.readString('"').join('');
        }
        else if (ch === "'") {
            return this.readString("'").join('');
        }
        else if (ch === '`') {
            return Node.create_root(this.readString('`'));
        }
        this.error('Bad string');
    };
    Parser.prototype.array = function () {
        var array = [];
        var ch = this.ch;
        if (ch === '[') {
            ch = this.next('[');
            this.white();
            if (ch === ']') {
                ch = this.next(']');
                return array;
            }
            while (ch) {
                array.push(this.expression());
                ch = this.white();
                if (ch === ']') {
                    ch = this.next(']');
                    return array;
                }
                this.next(',');
                ch = this.white();
            }
        }
        this.error('Bad array');
    };
    Parser.prototype.value = function () {
        var ch;
        this.white();
        ch = this.ch;
        switch (ch) {
            case '{': return this.object();
            case '[': return this.array();
            case '"':
            case "'":
            case '`': return this.string();
            case '-': return this.number();
            default:
                return ch >= '0' && ch <= '9' ? this.number() : this.identifier();
        }
    };
    /**
     * Get the function for the given operator.
     * A `.precedence` value is added to the function, with increasing
     * precedence having a higher number.
     * @return {function} The function that performs the infix operation
     */
    Parser.prototype.operator = function (opts) {
        var op = '';
        var opFn;
        var ch = this.white();
        var isIdentifierChar = Identifier.is_valid_start_char;
        while (ch) {
            if (isIdentifierChar(ch) || ch <= ' ' || ch === '' ||
                ch === '"' || ch === "'" || ch === '{' || ch === '(' ||
                ch === '`' || ch === ')' || (ch <= '9' && ch >= '0')) {
                break;
            }
            if (!opts.not_an_array && ch === '[') {
                break;
            }
            op += ch;
            ch = this.next();
            // An infix followed by the prefix e.g. a + @b
            // TODO: other prefix unary operators
            if (ch === '@') {
                break;
            }
            isIdentifierChar = Identifier.is_valid_continue_char;
        }
        if (op !== '') {
            if (opts.prefix && op === '-') {
                op = '&-';
            }
            opFn = operators[op];
            if (!opFn) {
                this.error("Bad operator: '" + op + "'.");
            }
        }
        return opFn;
    };
    /**
     * Filters
     * Returns what the Node interprets as an "operator".
     * e.g.
     *   <span data-bind="text: name | fit:20 | uppercase"></span>
     */
    Parser.prototype.filter = function () {
        var ch = this.next();
        var args = [];
        var nextFilter = function (v) { return v; };
        var name = this.name();
        if (!options.filters[name]) {
            options.onError('Cannot find filter by the name of: ' + name);
        }
        ch = this.white();
        while (ch) {
            if (ch === ':') {
                ch = this.next();
                args.push(this.expression('|'));
            }
            if (ch === '|') {
                nextFilter = this.filter();
                break;
            }
            if (ch === ',') {
                break;
            }
            ch = this.white();
        }
        var filter = function filter(value, ignored, context, globals, node) {
            var argValues = [value];
            for (var i = 0, j = args.length; i < j; ++i) {
                argValues.push(Node.value_of(args[i], context, globals, node));
            }
            return nextFilter(options.filters[name].apply(null, argValues));
        };
        // Lowest precedence.
        filter.precedence = 1;
        return filter;
    };
    /**
     * Parse an expression – builds an operator tree, in something like
     * Shunting-Yard.
     *   See: http://en.wikipedia.org/wiki/Shunting-yard_algorithm
     *
     * @return {function}   A function that computes the value of the expression
     *                      when called or a primitive.
     */
    Parser.prototype.expression = function (filterable) {
        var op;
        var nodes = [];
        var ch = this.white();
        while (ch) {
            // unary prefix operators
            op = this.operator({ prefix: true });
            if (op) {
                nodes.push(undefined); // LHS Tree node.
                nodes.push(op);
                ch = this.white();
            }
            if (ch === '(') {
                this.next();
                nodes.push(this.expression());
                this.next(')');
            }
            else {
                nodes.push(this.value());
            }
            ch = this.white();
            if (ch === ':' || ch === '}' || ch === ',' || ch === ']' ||
                ch === ')' || ch === '' || ch === '`' || (ch === '|' && filterable === '|')) {
                break;
            }
            // filters
            if (ch === '|' && this.lookahead() !== '|' && filterable) {
                nodes.push(this.filter());
                nodes.push(undefined);
                break;
            }
            // infix or postfix operators
            op = this.operator({ not_an_array: true });
            if (op === operators['?']) {
                this.ternary(nodes);
                break;
            }
            else if (op === operators['.']) {
                nodes.push(op);
                nodes.push(this.member());
                op = null;
            }
            else if (op === operators['[']) {
                nodes.push(op);
                nodes.push(this.expression());
                ch = this.next(']');
                op = null;
            }
            else if (op) {
                nodes.push(op);
            }
            ch = this.white();
            if (ch === ']' || (!op && ch === '(')) {
                break;
            }
        }
        if (nodes.length === 0) {
            return undefined;
        }
        var dereferences = this.dereferences();
        if (nodes.length === 1 && !dereferences.length) {
            return nodes[0];
        }
        for (var i = 0, j = dereferences.length; i < j; ++i) {
            var deref = dereferences[i];
            if (deref.constructor === Arguments) {
                nodes.push(operators.call);
            }
            else {
                nodes.push(operators['.']);
            }
            nodes.push(deref);
        }
        return new Expression(nodes);
    };
    Parser.prototype.ternary = function (nodes) {
        var ternary = new Ternary();
        ternary.yes = this.expression();
        this.next(':');
        ternary.no = this.expression();
        nodes.push(operators['?']);
        nodes.push(ternary);
    };
    /**
     * Parse the arguments to a function, returning an Array.
     *
     */
    Parser.prototype.funcArguments = function () {
        var args = [];
        var ch = this.next('(');
        while (ch) {
            ch = this.white();
            if (ch === ')') {
                this.next(')');
                return new Arguments(this, args);
            }
            else {
                args.push(this.expression());
                ch = this.white();
            }
            if (ch !== ')') {
                this.next(',');
            }
        }
        this.error('Bad arguments to function');
    };
    /**
     * The literal string reference `abc` in an `x.abc` expression.
     */
    Parser.prototype.member = function () {
        var member = '';
        var ch = this.white();
        var isIdentifierChar = Identifier.is_valid_start_char;
        while (ch) {
            if (!isIdentifierChar(ch)) {
                break;
            }
            member += ch;
            ch = this.next();
            isIdentifierChar = Identifier.is_valid_continue_char;
        }
        return member;
    };
    /**
     * A dereference applies to an identifer, being either a function
     * call "()" or a membership lookup with square brackets "[member]".
     * @return {fn or undefined}  Dereference function to be applied to the
     *                            Identifier
     */
    Parser.prototype.dereference = function () {
        var member;
        var ch = this.white();
        while (ch) {
            if (ch === '(') {
                // a(...) function call
                return this.funcArguments();
            }
            else if (ch === '[') {
                // a[x] membership
                this.next('[');
                member = this.expression();
                this.white();
                this.next(']');
                return member;
            }
            else if (ch === '.') {
                // a.x membership
                this.next('.');
                return this.member();
            }
            else {
                break;
            }
        }
    };
    Parser.prototype.dereferences = function () {
        var ch = this.white();
        var dereferences = [];
        var deref;
        while (ch) {
            deref = this.dereference();
            if (deref !== undefined) {
                dereferences.push(deref);
            }
            else {
                break;
            }
        }
        return dereferences;
    };
    Parser.prototype.identifier = function () {
        var token = '';
        var isIdentifierChar = Identifier.is_valid_start_char;
        var ch = this.white();
        while (ch) {
            if (!isIdentifierChar(ch)) {
                break;
            }
            token += ch;
            ch = this.next();
            isIdentifierChar = Identifier.is_valid_continue_char;
        }
        switch (token) {
            case 'true': return true;
            case 'false': return false;
            case 'null': return null;
            case 'undefined': return void 0;
            case 'function':
                throw new Error('Knockout: Anonymous functions are no longer supported, but `=>` lambdas are.');
            // return this.anonymous_fn();
        }
        return new Identifier(this, token, this.dereferences());
    };
    Parser.prototype.readBindings = function () {
        var key;
        var bindings = {};
        var sep;
        var expr;
        var ch = this.ch;
        while (ch) {
            key = this.name();
            sep = this.white();
            if (!sep || sep === ',') {
                if (sep) {
                    ch = this.next(',');
                }
                else {
                    ch = '';
                }
                // A "bare" binding e.g. "text"; substitute value of 'null'
                // so it becomes "text: null".
                bindings[key] = null;
            }
            else {
                if (key.indexOf('.') !== -1) {
                    // Namespaced – i.e.
                    //    `attr.css: x` becomes `attr: { css: x }`
                    //     ^^^ - key
                    key = key.split('.');
                    bindings[key[0]] = bindings[key[0]] || {};
                    if (key.length !== 2) {
                        options.onError('Binding ' + key + ' should have two parts (a.b).');
                    }
                    else if (bindings[key[0]].constructor !== Object) {
                        options.onError('Binding ' + key[0] + '.' + key[1] + ' paired with a non-object.');
                    }
                    ch = this.next(':');
                    this.objectAddValue(bindings[key[0]], key[1], this.expression(true));
                }
                else {
                    ch = this.next(':');
                    if (bindings[key] && typeof bindings[key] === 'object' && bindings[key].constructor === Object) {
                        // Extend a namespaced bindings e.g. we've previously seen
                        // on.x, now we're seeing on: { 'abc' }.
                        expr = this.expression(true);
                        if (typeof expr !== 'object' || expr.constructor !== Object) {
                            options.onError('Expected plain object for ' + key + ' value.');
                        }
                        else {
                            extend(bindings[key], expr);
                        }
                    }
                    else {
                        bindings[key] = this.expression(true);
                    }
                }
                this.white();
                if (this.ch) {
                    ch = this.next(',');
                }
                else {
                    ch = '';
                }
            }
        }
        return bindings;
    };
    Parser.prototype.valueAsAccessor = function (value, context, globals, node) {
        if (!value) {
            return function () { return value; };
        }
        if (typeof value === 'function') {
            return value;
        }
        if (value[Node.isExpressionOrIdentifierSymbol]) {
            return function () { return Node.value_of(value, context, globals, node); };
        }
        if (Array.isArray(value)) {
            return function () { return value.map(function (v) { return Node.value_of(v, context, globals, node); }); };
        }
        if (typeof (value) !== 'function') {
            return function () { return clonePlainObjectDeep(value); };
        }
        throw new Error('Value has cannot be converted to accessor: ' + value);
    };
    /**
    * Convert result[name] from a value to a function (i.e. `valueAccessor()`)
    * @param  {object} result [Map of top-level names to values]
    * @return {object}        [Map of top-level names to functions]
    *
    * Accessors may be one of (below) constAccessor, identifierAccessor,
    * expressionAccessor, or nodeAccessor.
    */
    Parser.prototype.convertToAccessors = function (result, context, globals, node) {
        var _this = this;
        objectForEach(result, function (name, value) {
            if (value instanceof Identifier) {
                // Return a function that, with no arguments returns
                // the value of the identifier, otherwise sets the
                // value of the identifier to the first given argument.
                Object.defineProperty(result, name, {
                    value: function (optionalValue, options$$1) {
                        var currentValue = value.get_value(undefined, context, globals, node);
                        if (arguments.length === 0) {
                            return currentValue;
                        }
                        var unchanged = optionalValue === currentValue;
                        if (options$$1 && options$$1.onlyIfChanged && unchanged) {
                            return;
                        }
                        return value.set_value(optionalValue, context, globals);
                    }
                });
            }
            else {
                result[name] = _this.valueAsAccessor(value, context, globals, node);
            }
        });
        return result;
    };
    Parser.prototype.preparse = function (source) {
        if (source === void 0) { source = ''; }
        var preparsers = options.bindingStringPreparsers || [];
        return preparsers.reduce(function (acc, fn) { return fn(acc); }, source.trim());
    };
    Parser.prototype.runParse = function (source, fn) {
        this.text = this.preparse(source);
        this.at = 0;
        this.ch = ' ';
        try {
            var result = fn();
            this.white();
            if (this.ch) {
                this.error('Syntax Error');
            }
            return result;
        }
        catch (e) {
            options.onError(e);
        }
    };
    /**
     * Get the bindings as name: accessor()
     * @param  {string} source The binding string to parse.
     * @return {object}        Map of name to accessor function.
     */
    Parser.prototype.parse = function (source, context, globals, node) {
        var _this = this;
        if (context === void 0) { context = {}; }
        if (globals === void 0) { globals = {}; }
        if (!source) {
            return function () { return null; };
        }
        this.currentContextGlobals = [context, globals, node];
        var parseFn = function () { return _this.readBindings(); };
        var bindingAccessors = this.runParse(source, parseFn);
        return this.convertToAccessors(bindingAccessors, context, globals, node);
    };
    /**
     * Return a function that evaluates and returns the result of the expression.
     */
    Parser.prototype.parseExpression = function (source, context, globals, node) {
        var _this = this;
        if (context === void 0) { context = {}; }
        if (globals === void 0) { globals = {}; }
        if (!source) {
            return function () { return ''; };
        }
        this.currentContextGlobals = [context, globals, node];
        var parseFn = function () { return _this.expression(true); };
        var bindingAccessors = this.runParse(source, parseFn);
        return this.valueAsAccessor(bindingAccessors, context, globals, node);
    };
    return Parser;
}());

/* eslint no-cond-assign: 0 */
// The following regular expressions will be used to split an object-literal string into tokens
// These characters have special meaning to the parser and must not appear in the middle of a
// token, except as part of a string.
var specials = ',"\'`{}()/:[\\]';
var bindingToken = RegExp([
    // These match strings, either with double quotes, single quotes, or backticks
    '"(?:\\\\.|[^"])*"',
    "'(?:\\\\.|[^'])*'",
    '`(?:\\\\.|[^`])*`',
    // Match C style comments
    '/\\*(?:[^*]|\\*+[^*/])*\\*+/',
    // Match C++ style comments
    '//.*\n',
    // Match a regular expression (text enclosed by slashes), but will also match sets of divisions
    // as a regular expression (this is handled by the parsing loop below).
    '/(?:\\\\.|[^/])+/\\w*',
    // Match text (at least two characters) that does not contain any of the above special characters,
    // although some of the special characters are allowed to start it (all but the colon and comma).
    // The text can contain spaces, but leading or trailing spaces are skipped.
    '[^\\s:,/][^' + specials + ']*[^\\s' + specials + ']',
    // Match any non-space character not matched already. This will match colons and commas, since they're
    // not matched by "everyThingElse", but will also match any other single character that wasn't already
    // matched (for example: in "a: 1, b: 2", each of the non-space characters will be matched by oneNotSpace).
    '[^\\s]'
].join('|'), 'g');
// Match end of previous token to determine whether a slash is a division or regex.
var divisionLookBehind = /[\])"'A-Za-z0-9_$]+$/;
var keywordRegexLookBehind = { 'in': 1, 'return': 1, 'typeof': 1 };
/**
 * Break a binding string (data-bind='x: val, y: ..') into a stable array
 * of {key: value}.
 */
function parseObjectLiteral(objectLiteralString) {
    // Trim leading and trailing spaces from the string
    var str = stringTrim(objectLiteralString);
    // Trim braces '{' surrounding the whole object literal
    if (str.charCodeAt(0) === 123)
        str = str.slice(1, -1);
    // Add a newline to correctly match a C++ style comment at the end of the string and
    // add a comma so that we don't need a separate code block to deal with the last item
    str += '\n,';
    // Split into tokens
    var result = [];
    var toks = str.match(bindingToken);
    var key;
    var values = [];
    var depth = 0;
    if (toks.length <= 1) {
        return [];
    }
    for (var i = 0, tok; tok = toks[i]; ++i) {
        var c = tok.charCodeAt(0);
        // A comma signals the end of a key/value pair if depth is zero
        if (c === 44) { // ","
            if (depth <= 0) {
                result.push((key && values.length) ? {
                    key: key,
                    value: values.join('')
                } : {
                    'unknown': key || values.join('')
                });
                key = depth = 0;
                values = [];
                continue;
            }
            // Simply skip the colon that separates the name and value
        }
        else if (c === 58) { // ":"
            if (!depth && !key && values.length === 1) {
                key = values.pop();
                continue;
            }
            // A set of slashes is initially matched as a regular expression, but could be division
        }
        else if (c === 47 && tok.length > 1 && (tok.charCodeAt(1) === 47 || tok.charCodeAt(1) === 42)) { // "//" or "/*"
            // skip comments
            continue;
        }
        else if (c === 47 && i && tok.length > 1) { // "/"
            // Look at the end of the previous token to determine if the slash is actually division
            var match = toks[i - 1].match(divisionLookBehind);
            if (match && !keywordRegexLookBehind[match[0]]) {
                // The slash is actually a division punctuator; re-parse the remainder of the string (not including the slash)
                str = str.substr(str.indexOf(tok) + 1);
                toks = str.match(bindingToken);
                i = -1;
                // Continue with just the slash
                tok = '/';
            }
            // Increment depth for parentheses, braces, and brackets so that interior commas are ignored
        }
        else if (c === 40 || c === 123 || c === 91) { // '(', '{', '['
            ++depth;
        }
        else if (c === 41 || c === 125 || c === 93) { // ')', '}', ']'
            --depth;
            // The key will be the first token; if it's a string, trim the quotes
        }
        else if (!key && !values.length && (c === 34 || c === 39)) { // '"', "'"
            tok = tok.slice(1, -1);
        }
        values.push(tok);
    }
    return result;
}

export { Parser, Identifier, Arguments, Node, parseObjectLiteral };
//# sourceMappingURL=utils.parser.js.map
